import * as Sentry from "@sentry/nuxt";
import {
  IGNORED_ERROR_TARGETS,
  IGNORED_STACK_FILENAMES,
  isEmptyNonErrorObject,
} from "./constants/ignorable-errors";

const runtime = useRuntimeConfig().public;
const config = runtime.sentry;

/**
 * Whether this tab is in a measurement run of the annotation sync.
 *
 * @see plugins/09-sync-telemetry.client.ts, which reads the same switch.
 */
const isMeasuringSync = (): boolean => {
  try {
    if (window.localStorage.getItem("annotationSyncTracing") === "1") return true;
  } catch {
    // Storage disabled by the browser. Not a reason to fail a page load.
  }

  return config.environment !== "production";
};

Sentry.init({
  dsn: config.dsn,

  /**
   * The API is on another origin, so without this no `sentry-trace` header
   * reaches it and a write is two unrelated traces: the browser's and the
   * API's. Scoped to that one origin, so nothing is sent to Pusher, Daily,
   * Stripe or Mapbox.
   */
  tracePropagationTargets: [runtime.apiBase].filter(Boolean),

  /**
   * A propagation between participants has to be sampled whole or it cannot be
   * timed, and a p95 over the twenty scripted actions of a measurement run
   * cannot be taken at one trace in ten. Everything else stays where it was.
   *
   * Keyed on an attribute and not on the op: the sampling context is built from
   * a span's attributes alone, and the op is applied to the span after the
   * decision has been made, so a sampler asking for the op would quietly match
   * nothing and take a tenth of what it was meant to (LWR-26720).
   */
  tracesSampler: (context) => {
    const isSyncSpan =
      context.attributes?.["annotation.sync"] === true ||
      Boolean(context.name?.startsWith("annotationSync."));

    if (isSyncSpan) return isMeasuringSync() ? 1.0 : 0.05;

    return context.inheritOrSampleWith(0.1);
  },

  replaysSessionSampleRate: 0.25,
  replaysOnErrorSampleRate: 0.5,

  integrations: [
    Sentry.replayIntegration(),
    Sentry.replayCanvasIntegration(),
  ],

  environment: config.environment,
  debug: false,

  beforeSend(event, hint) {
    if (isEmptyNonErrorObject(hint?.originalException)) {
      return null;
    }

    const extraData = event.extra as Record<string, any> | undefined;
    // Newer Sentry SDK versions serialize non-Error rejections with properties
    // flat on event.extra; older ones nest them under __serialized__.
    const target = (extraData?.target ??
      extraData?.__serialized__?.target) as string | undefined;
    const message = event.message ?? "";
    const exceptionValues = event.exception?.values ?? [];
    const stackFilenames = exceptionValues.flatMap(
      (v) => v.stacktrace?.frames?.map((f) => f.filename ?? "") ?? [],
    );

    const normalizedTarget = target?.toLowerCase() ?? "";
    const normalizedMessage = message.toLowerCase();
    const normalizedFilenames = stackFilenames.map((f) => f.toLowerCase());
    // UnhandledRejection errors land in exception.values[].value, not event.message
    const normalizedExceptionValues = exceptionValues.map((v) =>
      (v.value ?? "").toLowerCase(),
    );

    const shouldIgnore =
      IGNORED_ERROR_TARGETS.some(
        (p) =>
          normalizedTarget.includes(p) ||
          normalizedMessage.includes(p) ||
          normalizedExceptionValues.some((v) => v.includes(p)),
      ) ||
      IGNORED_STACK_FILENAMES.some((p) =>
        normalizedFilenames.some((f) => f.includes(p)),
      );

    return shouldIgnore ? null : event;
  },
});
